module Shared.PasswordReset.ResetPassword

open System

open Shared.Zxcvbn
open Illy.SafeStack

type InitData =
    {
        IsTokenValid: bool
        PasswordConfig: PasswordConfig
        IsMFA: bool
    }

type DTO =
    {
        Password: string option
        ConfirmPassword: string option
    }

module Validations = 
    open Illy.SafeStack.SharedValidations
    open Illy.SafeStack.CompositionalValidation
    open Illy.SafeStack.ROP
    open Illy.SafeStack.Validation
    open Shared.Validations.PasswordStrength.Validations

    type PasswordValidations =
        | PasswordStrength of PasswordStrengthValidations
        | CannotReusePassword of reuseAfter: int

    module PasswordValidations =
        let toString =
            function
            | PasswordStrength v -> PasswordStrengthValidations.toString v
            | CannotReusePassword reuseAfter -> sprintf "The chosen password was one of your last %d passwords." reuseAfter

    type ConfirmPasswordValidations = 
        | StringValidations of StringValidations 
        | PasswordsMustMatch 

    module ConfirmPasswordValidations = 
        let toString = 
            function 
            | ConfirmPasswordValidations.StringValidations v -> v.toString()
            | ConfirmPasswordValidations.PasswordsMustMatch -> "Please ensure you have typed the same password twice."

    type ResetPasswordValidations =
        | Password of PasswordValidations
        | ConfirmPassword of ConfirmPasswordValidations

    module ResetPasswordValidations =
        let toString =
            function
            | ResetPasswordValidations.Password x -> PasswordValidations.toString x
            | ResetPasswordValidations.ConfirmPassword x -> ConfirmPasswordValidations.toString x

    let safelyTakeItemsFromList count items =
        if List.length items < count then items
        else List.take count items

    let validatePassword scorePassword passwordConfig password =
        validate {
            return! (validatePasswordStrength passwordConfig scorePassword >-> PasswordStrength) password
        }

    let validateConfirmPassword (config: PasswordStrengthConfig) password confirmPassword = 
        validate {
            return! (validateString (config.MaxLength |> Option.defaultValue 250) true >-> ConfirmPasswordValidations.StringValidations) confirmPassword
            return! !!(error ConfirmPasswordValidations.PasswordsMustMatch)
                >>= passIfNone password 
                >>= passIfNone confirmPassword 
                >>= conditional pass (fun ((_,p), cp) -> p = cp)
                |> mapResult (fst >> fst)
        }

    let validateDTO scorePassword config (dto: DTO) = 
        validate {
            return! (validatePassword scorePassword config >-> Password) dto.Password
            return! (validateConfirmPassword config.PasswordStrength dto.Password >-> ConfirmPassword) dto.ConfirmPassword
        }

type FailureReason = 
    | InvalidToken
    | FailedValidations of Validation.ValidationResult<Validations.ResetPasswordValidations, Validation.NoWarning>